// src/components/FinalniObjednavka.jsx
import React, { useMemo, useState, useCallback } from "react";
import { spoctiRakevCeny } from "../utils/rakve";
import { useFormData } from "../context/FormDataContext";


const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const MS_HOUR = 1000 * 60 * 60;
const MS_DAY = 24 * MS_HOUR;

function parseDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const safeTime =
    timeStr && String(timeStr).trim() ? String(timeStr).trim() : "00:00";
  const t = new Date(`${dateStr}T${safeTime}:00`);
  return Number.isNaN(t.getTime()) ? null : t;
}

function startedDays(fromDT, toDT) {
  if (!fromDT || !toDT) return null;
  const ms = toDT.getTime() - fromDT.getTime();
  if (ms <= 0) return 0;
  return Math.max(1, Math.ceil(ms / MS_DAY));
}

function splitCoolingDays({ deathDT, fridgeStartDT, endDT, maxFridgeDays = 7 }) {
  if (!fridgeStartDT || !endDT) {
    return {
      fridgeDays: null,
      freezerDays: null,
      fridgeEndDT: null,
      freezerStartDT: null,
      fridgeDeadlineDT: deathDT
        ? new Date(deathDT.getTime() + maxFridgeDays * MS_DAY)
        : null,
    };
  }

  if (!deathDT) {
    return {
      fridgeDays: startedDays(fridgeStartDT, endDT),
      freezerDays: 0,
      fridgeEndDT: endDT,
      freezerStartDT: null,
      fridgeDeadlineDT: null,
    };
  }

  const fridgeDeadlineDT = new Date(deathDT.getTime() + maxFridgeDays * MS_DAY);

  const fridgeEndDT = new Date(
    Math.min(endDT.getTime(), fridgeDeadlineDT.getTime())
  );
  const fridgeDays = startedDays(fridgeStartDT, fridgeEndDT);

  let freezerDays = 0;
  let freezerStartDT = null;

  if (endDT.getTime() > fridgeDeadlineDT.getTime()) {
    freezerStartDT = new Date(
      Math.max(fridgeStartDT.getTime(), fridgeDeadlineDT.getTime())
    );
    freezerDays = startedDays(freezerStartDT, endDT);
  }

  return {
    fridgeDays,
    freezerDays,
    fridgeEndDT,
    freezerStartDT,
    fridgeDeadlineDT,
  };
}

const cardStyle = {
  border: "1px solid #333",
  borderRadius: 10,
  padding: 12,
  marginBottom: 12,
};
const titleStyle = { fontWeight: 800, marginBottom: 6 };
const textStyle = { fontSize: 13, opacity: 0.9, lineHeight: 1.6 };

const pick = (...vals) => {
  for (const v of vals) {
    const s = (v ?? "").toString().trim();
    if (s) return s;
  }
  return "";
};

const fullName = (o, fallback1, fallback2) => {
  const j = pick(o?.jmeno, fallback1);
  const p = pick(o?.prijmeni, fallback2);
  return [j, p].filter(Boolean).join(" ").trim();
};

const PREVOZ_IDS = ["prevoz_1", "prevoz_2", "prevoz_3"];
const PREVOZ_TITLES = {
  prevoz_1: "Převoz 1 – úmrtí → chladírna",
  prevoz_2: "Převoz 2 – chladírna → obřad",
  prevoz_3: "Převoz 3 – obřad → pohřbení / krematorium",
};

export default function FinalniObjednavka() {
  const {
    formData,
    setFormData,
    updateMany,
    updateValue,
    currentOrderId,
    saveOrder,
  } = useFormData();

  const rakevNazev = String(
    formData?.rakve?.nazev ||
      formData?.rakve?.typRakve ||
      formData?.typRakve?.nazev ||
      ""
  ).trim();

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const sluzby = Array.isArray(formData?.sluzby) ? formData.sluzby : [];
  const vstup = formData?.vstupniInformace || {};
  const kp = formData?.kremacePohreb || {};
  const obrad = formData?.obrad || {};
  const zesn = formData?.zesnulaOsoba || {};
  const vypr = formData?.vypravitel || {};
  const kvetiny = formData?.kvetiny || {};

  // ✅ PŘEVOZY
  const prevozy = Array.isArray(formData?.prevozy) ? formData.prevozy : [];
  const prevozyById = useMemo(() => {
    const m = new Map();
    for (const p of prevozy) if (p?.id) m.set(String(p.id), p);
    return m;
  }, [prevozy]);

  const prevoz1 = prevozyById.get("prevoz_1") || {};
  const prevoz2 = prevozyById.get("prevoz_2") || {};
  const prevoz3 = prevozyById.get("prevoz_3") || {};

  const totalKm = useMemo(() => {
    return round2(prevozy.reduce((sum, p) => sum + (Number(p?.km) || 0), 0));
  }, [prevozy]);

  // --- typ (kremace/pohřeb) ---
  const typLower = String(
    kp?.typ || kp?.zpusob || kp?.volba || kp?.druh || "kremace"
  )
    .toLowerCase()
    .trim();

  const isPohreb = typLower.includes("poh");
  const isKremace = typLower.includes("krem");
  const kremaceBezObradu = !!kp?.kremaceBezObradu;

  // --- EVENT (datum/čas/místo) – primárně z OBŘAD, fallback KP ---
  const datumEvent = kremaceBezObradu
    ? pick(kp?.datumKremace)
    : pick(obrad?.datumObradu, kp?.datumObradu);

  const casEvent = kremaceBezObradu
    ? pick(kp?.casKremace)
    : pick(obrad?.casObradu, kp?.casObradu);

  const mistoEvent =
    pick(obrad?.mistoObradu, kp?.mistoObradu, kp?.obradMisto, kp?.misto) ||
    (kremaceBezObradu ? "Kremace bez obřadu" : "—");

  const cil = isPohreb
    ? pick(kp?.hrbitov, kp?.mistoPohrbeni, kp?.misto, "—")
    : pick(kp?.krematorium, kp?.mistoKremace, kp?.misto, "—");

  // --- ZESNULÝ ---
  const zesnuly = useMemo(() => {
    return (
      fullName(zesn, vstup?.jmenoZesnuleho, vstup?.prijmeniZesnuleho) ||
      fullName(zesn, zesn?.jmeno, zesn?.prijmeni) ||
      "—"
    );
  }, [zesn, vstup]);

  const datumUmrti = pick(zesn?.datumUmrti, vstup?.datumUmrti);
  const casUmrti = pick(zesn?.casUmrti, vstup?.casUmrti);
  const umrtiMisto = pick(
    zesn?.mistoUmrti,
    vstup?.mistoUmrti,
    vstup?.adresaUmrti,
    "—"
  );
  const trvaleBydliste = pick(
    zesn?.adresaTrvalehoBydliste,
    zesn?.trvaleBydliste,
    zesn?.trvale_bydliste,
    vstup?.adresaZesnuleho,
    "—"
  );
  const datumNarozeni = pick(zesn?.datumNarozeni, vstup?.datumNarozeni);

  // --- VYPRAVITEL ---
  const vypravitelJmeno = fullName(vypr, "", "") || "—";
  const vypravitelTel = pick(
    vypr?.telefon,
    vstup?.telefonVypravitele,
    vstup?.telefon,
    "—"
  );
  const vypravitelEmail = pick(
    vypr?.email,
    vstup?.emailVypravitele,
    vstup?.email,
    "—"
  );
  const vypravitelAdresa = pick(vypr?.adresa, "—");
  const vypravitelVztah = pick(vypr?.vztahKZesnulemu, vypr?.vztah, "—");
  const vypravitelNar = pick(vypr?.datumNarozeni, "—");
  const vypravitelRcIco = pick(vypr?.rodneCisloIco, "—");

  const ko = vypr?.kontaktniOsoba || {};
  const hasKO = Boolean(
    ko?.jmeno ||
      ko?.prijmeni ||
      ko?.role ||
      ko?.telefon ||
      ko?.email ||
      ko?.adresa ||
      ko?.trvaleBydliste ||
      ko?.prechodneBydliste
  );
  const koJmeno = fullName(ko, "", "") || "—";
  const koRole = pick(ko?.role, "—");
  const koTel = pick(ko?.telefon, "—");
  const koEmail = pick(ko?.email, "—");
  const koAdresa = pick(ko?.adresa, pick(ko?.trvaleBydliste, "—"));
  const koPrechodne = pick(ko?.prechodneBydliste, "");

  // --- RAKVE ---
  const vybranaRakveId =
    formData?.vybranaRakveId != null ? String(formData.vybranaRakveId) : "";

  const rakve = formData?.rakve || {};
  const skladRakvi = Array.isArray(formData?.skladRakvi)
    ? formData.skladRakvi
    : [];

  // --- SLUŽBY: výpočet ---
  const toNum = (v) => {
    if (v === null || typeof v === "undefined") return 0;
    const s = String(v).replace(",", ".").replace(/[^\d.\-]/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const pickRate = (s) => {
    const r1 = toNum(s?.dphRate);
    if (r1 > 0 && r1 < 1) return r1;

    const p = toNum(s?.dphPct);
    if (p >= 1 && p <= 100) return p / 100;
    if (p > 0 && p < 1) return p;

    const d = toNum(s?.dph);
    if (d >= 1 && d <= 100) return d / 100;
    if (d > 0 && d < 1) return d;

    return 0;
  };

  const pickQty = (s) => toNum(s?.mnozstvi ?? s?.qty ?? s?.quantity ?? 1);

  const pickUnit = (s) => {
    return toNum(
      s?.cenaBezDph ??
        s?.cenaJednBezDph ??
        s?.unitPrice ??
        s?.price ??
        s?.cena ??
        0
    );
  };

  const pickSubtotalBez = (s, qty, unit) => {
    const a = toNum(s?.celkemBezDph);
    if (a > 0) return a;

    const b = toNum(s?.bezDph);
    if (b > 0) return b;

    return Math.round(qty * unit * 100) / 100;
  };

  const sluzbyWithTotals = useMemo(() => {
    return (Array.isArray(sluzby) ? sluzby : [])
      .filter((x) => x && (x.id || x.key))
      .map((x) => {
        const id = String(x.id || x.key || "");
        const nazev = x?.nazev || x?.name || id;
        const jednotka = x?.jednotka || x?.unit || "";

        const qty = pickQty(x);
        const unit = pickUnit(x);
        const rate = pickRate(x);

        const bez = pickSubtotalBez(x, qty, unit);
        const dphKc = Math.round(bez * rate * 100) / 100;
        const sDph = Math.round((bez + dphKc) * 100) / 100;

        return {
          id,
          nazev,
          jednotka,
          mnozstvi: Math.round(qty * 100) / 100,
          cenaJednBezDph: Math.round(unit * 100) / 100,
          bezDph: Math.round(bez * 100) / 100,
          dphKc,
          sDph,
          dphPct: Math.round(rate * 10000) / 100,
        };
      });
  }, [sluzby]);

  const sluzbyWithTotalsSafe =
    typeof sluzbyWithTotals !== "undefined" && Array.isArray(sluzbyWithTotals)
      ? sluzbyWithTotals
      : Array.isArray(sluzby)
      ? sluzby
      : [];

  const sluzbyTotalBez = useMemo(
    () =>
      round2(
        sluzbyWithTotalsSafe.reduce(
          (sum, s) => sum + toNum(s.bezDph ?? s.cenaBezDph ?? s.cenaBez ?? 0),
          0
        )
      ),
    [sluzbyWithTotalsSafe]
  );

  const sluzbyTotalDph = useMemo(
    () =>
      round2(
        sluzbyWithTotalsSafe.reduce(
          (sum, s) => sum + toNum(s.dphKc ?? s.dph ?? 0),
          0
        )
      ),
    [sluzbyWithTotalsSafe]
  );

  const sluzbyTotalSDph = useMemo(
    () =>
      round2(
        sluzbyWithTotalsSafe.reduce(
          (sum, s) => sum + toNum(s.sDph ?? s.cenaSDph ?? 0),
          0
        )
      ),
    [sluzbyWithTotalsSafe]
  );

  const rakveBez =
    Number(
      rakve?.cenaBezDPH ??
        rakve?.cenaBezDph ??
        rakve?.cena_bez_dph ??
        rakve?.cena ??
        0
    ) || 0;
  const rakveDphPct =
    Number(
      rakve?.sazbaDPH ??
        rakve?.dph ??
        rakve?.sazba_dph ??
        (Number(rakve?.cenaBezDPH ?? rakve?.cenaBezDph ?? 0) > 0 ? 12 : 0)
    ) || 0;
  const rakveDph = round2((rakveBez * rakveDphPct) / 100);
  const rakveSDph = round2(rakveBez + rakveDph);

  const kvetinyRows = useMemo(() => {
    const arr = Array.isArray(kvetiny?.polozky) ? kvetiny.polozky : [];
    return arr
      .map((r) => {
        const pocet = Math.max(0, toNum(r?.pocet ?? 0));
        const cenaKsBez = round2(toNum(r?.cenaKs ?? 0));
        const cenaKsSDPH = round2(
          r?.cenaKsSDPH != null && String(r.cenaKsSDPH).trim() !== ""
            ? toNum(r.cenaKsSDPH)
            : cenaKsBez * (1 + toNum(r?.dph ?? 0) / 100)
        );
        const dphPct = Math.max(0, toNum(r?.dph ?? 0));
        const bezDph = round2(pocet * cenaKsBez);
        const sDph = round2(pocet * cenaKsSDPH);
        const dphKc = round2(sDph - bezDph);

        return {
          id: String(r?.id ?? ""),
          nazev: String(r?.nazev ?? "").trim(),
          pocet,
          cenaKs: cenaKsBez,
          cenaKsSDPH,
          dph: dphPct,
          bezDph,
          dphKc,
          sDph,
          poznamka: String(r?.poznamka ?? "").trim(),
        };
      })
      .filter(
        (r) => r.nazev || r.pocet || r.cenaKs || r.cenaKsSDPH || r.poznamka
      );
  }, [kvetiny]);

  const kvetinyTotalBez = useMemo(
    () => round2(kvetinyRows.reduce((sum, r) => sum + toNum(r.bezDph), 0)),
    [kvetinyRows]
  );

  const kvetinyTotalDph = useMemo(
    () => round2(kvetinyRows.reduce((sum, r) => sum + toNum(r.dphKc), 0)),
    [kvetinyRows]
  );

  const kvetinyTotalSDph = useMemo(
    () => round2(kvetinyRows.reduce((sum, r) => sum + toNum(r.sDph), 0)),
    [kvetinyRows]
  );

  const grandBez = round2(
    sluzbyTotalBez + (rakveBez > 0 ? rakveBez : 0) + kvetinyTotalBez
  );
  const grandDph = round2(
    sluzbyTotalDph + (rakveBez > 0 ? rakveDph : 0) + kvetinyTotalDph
  );
  const grandSDph = round2(
    sluzbyTotalSDph + (rakveBez > 0 ? rakveSDph : 0) + kvetinyTotalSDph
  );

  // --- CHLAZENÍ ---
  const chlStart = useMemo(() => {
    return parseDateTime(pick(prevoz1?.datum), pick(prevoz1?.cas));
  }, [prevoz1?.datum, prevoz1?.cas]);

  const chlEnd = useMemo(() => {
    const endObrad = parseDateTime(
      pick(obrad?.datumObradu, kp?.datumObradu),
      pick(obrad?.casObradu, kp?.casObradu)
    );

    const endKremace = kremaceBezObradu
      ? parseDateTime(kp?.datumKremace, kp?.casKremace)
      : null;

    return endObrad || endKremace || null;
  }, [
    obrad?.datumObradu,
    obrad?.casObradu,
    kp?.datumObradu,
    kp?.casObradu,
    kremaceBezObradu,
    kp?.datumKremace,
    kp?.casKremace,
  ]);

  const deathDT = useMemo(
    () => parseDateTime(datumUmrti, casUmrti),
    [datumUmrti, casUmrti]
  );

  const cooling = useMemo(
    () =>
      splitCoolingDays({
        deathDT,
        fridgeStartDT: chlStart,
        endDT: chlEnd,
        maxFridgeDays: 7,
      }),
    [deathDT, chlStart, chlEnd]
  );

  const daysLednice = cooling.fridgeDays;
  const daysMrazak = cooling.freezerDays;

  const fmtDT = (dt) => {
    if (!dt) return "—";
    try {
      return dt.toLocaleString("cs-CZ");
    } catch {
      return "—";
    }
  };

  // ===== ULOŽIT + ODEČET RAKVE =====
  const handleUlozitObjednavku = useCallback(async () => {
    setSaveMsg("");
    setSaving(true);

    try {
      const out = await saveOrder();
      const savedId = String(
        out?.id ||
        out?.meta?.id ||
        currentOrderId ||
        formData?.id ||
        formData?.orderId ||
        ""
      ).trim();

      if (vybranaRakveId) {
        setFormData((prev) => {
          const list = Array.isArray(prev?.skladRakvi) ? prev.skladRakvi : [];
          const next = list.map((x) => {
            const xid = x?.id != null ? String(x.id) : "";
            if (xid !== String(vybranaRakveId)) return x;

            const cur = Number(x?.stavKs ?? 0) || 0;
            const newStav = Math.max(0, cur - 1);
            return { ...x, stavKs: newStav };
          });
          return { ...prev, skladRakvi: next };
        });

        const selectedRow = (Array.isArray(skladRakvi) ? skladRakvi : []).find(
          (x) => String(x?.id ?? "") === String(vybranaRakveId)
        );
        const cur = Number(selectedRow?.stavKs ?? 0) || 0;
        const after = Math.max(0, cur - 1);

        if (after <= 0) {
          const patchObj = {
            vybranaRakveId: "",
            "rakve.id": "",
            "rakve.kod": "",
            "rakve.typRakve": "",
            "rakve.nazev": "",
            "rakve.cenaBezDPH": 0,
            "rakve.sazbaDPH": 0,
          };
          if (updateMany) updateMany(patchObj);
          else if (updateValue)
            Object.entries(patchObj).forEach(([k, v]) => updateValue(k, v));
        }
      }

      setSaveMsg(`✅ Uloženo: ${savedId || "bez ID"}`);
    } catch (e) {
      setSaveMsg(`❌ Uložení selhalo: ${e?.message || "neznámá chyba"}`);
    } finally {
      setSaving(false);
    }
  }, [
    saveOrder,
    currentOrderId,
    formData,
    vybranaRakveId,
    setFormData,
    skladRakvi,
    updateMany,
    updateValue,
  ]);

  // ✅ PŘEVOZY: řádek + zobrazení mezizastávky a rozpadu km
  const renderPrevozRow = (id, p) => {
    const from = pick(p?.mistoOdvozu, p?.kmFrom, "—");
    const to = pick(p?.mistoDovozu, p?.kmTo, "—");
    const mid = pick(p?.mezizastavka, "");
    const dt = pick(p?.datum, "—");
    const tm = pick(p?.cas, "");
    const spz = pick(p?.spz, "—");
    const ridic = pick(p?.ridic, "—");
    const km = pick(p?.km, "—");

    return (
      <div
        key={id}
        style={{ border: "1px solid #444", borderRadius: 10, padding: 10 }}
      >
        <div style={{ fontWeight: 800 }}>{PREVOZ_TITLES[id] || id}</div>

        <div style={textStyle}>
          <div>
            <b>Odkud:</b> {from}
          </div>

          {id === "prevoz_3" && mid ? (
            <div>
              <b>Mezizastávka:</b> {mid}
            </div>
          ) : null}

          <div>
            <b>Kam:</b> {to}
          </div>

          <div>
            <b>Datum/čas:</b> {dt} {tm ? `• ${tm}` : ""}
          </div>

          <div>
            <b>SPZ:</b> {spz} • <b>Řidič:</b> {ridic}
          </div>

          <div>
            <b>Km:</b> {km}
          </div>

          {p?.kmParts?.a ? (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>
              <b>Rozpad km:</b>
              <div style={{ marginTop: 2 }}>
                • Úsek A: {p.kmParts.a.km} km ({p.kmParts.a.from} →{" "}
                {p.kmParts.a.to})
              </div>
              {p?.kmParts?.b ? (
                <div style={{ marginTop: 2 }}>
                  • Úsek B: {p.kmParts.b.km} km ({p.kmParts.b.from} →{" "}
                  {p.kmParts.b.to})
                </div>
              ) : null}
            </div>
          ) : null}

          {p?.kmUpdatedAt ? (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
              <b>Aktualizace km:</b>{" "}
              {new Date(p.kmUpdatedAt).toLocaleString("cs-CZ")}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10 }}>
        Finální objednávka {currentOrderId ? `(${currentOrderId})` : ""}
      </h2>

      <div
        style={{
          margin: "10px 0 14px",
          padding: 12,
          border: "1px solid #eee",
          borderRadius: 14,
        }}
      >
        <label style={{ fontWeight: 900, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={formData?.fakturovano === true}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                fakturovano: e.target.checked,
              }))
            }
            style={{ marginRight: 10, transform: "scale(1.1)" }}
          />
          Fakturováno
        </label>
        <div style={{ marginTop: 6, opacity: 0.7, fontSize: 12 }}>
          Aktivní = nezafakturováno, Vše = historie.
        </div>
      </div>


      <div
        style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}
      >
        <button type="button" onClick={handleUlozitObjednavku} disabled={saving}>
          {saving
            ? "Ukládám..."
            : currentOrderId
            ? "Uložit změny"
            : "Uložit novou zakázku"}
        </button>

        {saveMsg ? (
          <div style={{ fontSize: 13, opacity: 0.9 }}>{saveMsg}</div>
        ) : null}
      </div>

      {/* ZÁKLAD */}
      <div style={cardStyle}>
        <div style={titleStyle}>Základ</div>
        <div style={textStyle}>
          <div>
            <b>Zesnulý/á:</b> {zesnuly}
          </div>
          <div>
            <b>Datum narození:</b> {datumNarozeni || "—"}
          </div>
          <div>
            <b>Trvalé bydliště:</b> {trvaleBydliste}
          </div>

          <div>
            <b>Datum úmrtí:</b> {datumUmrti || "—"} {casUmrti ? `• ${casUmrti}` : ""}
          </div>
          <div>
            <b>Místo úmrtí:</b> {umrtiMisto}
          </div>

          <div>
            <b>{kremaceBezObradu ? "Datum kremace:" : "Datum obřadu:"}</b>{" "}
            {datumEvent || "—"} {casEvent ? `• ${casEvent}` : ""}
          </div>

          <div>
            <b>Místo:</b> {mistoEvent || "—"}
          </div>

          <div>
            <b>Cíl:</b> {cil}{" "}
            <span style={{ marginLeft: 8, opacity: 0.75 }}>
              ({isPohreb ? "pohřeb" : isKremace ? "kremace" : "typ nejasný"})
            </span>
          </div>
        </div>
      </div>

      {/* VYPRAVITEL */}
      <div style={cardStyle}>
        <div style={titleStyle}>Vypravitel</div>
        <div style={textStyle}>
          <div>
            <b>Jméno:</b> {vypravitelJmeno}
          </div>
          <div>
            <b>Datum narození:</b> {vypravitelNar}
          </div>
          <div>
            <b>Rodné číslo / IČO:</b> {vypravitelRcIco}
          </div>
          <div>
            <b>Vztah k zesnulému:</b> {vypravitelVztah}
          </div>
          <div>
            <b>Adresa:</b> {vypravitelAdresa}
          </div>
          <div>
            <b>Telefon:</b> {vypravitelTel} • <b>Email:</b> {vypravitelEmail}
          </div>
        </div>
      </div>

      {/* PŘEVOZY */}
      <div style={cardStyle}>
        <div style={titleStyle}>Převozy</div>

        <div style={{ display: "grid", gap: 10 }}>
          {PREVOZ_IDS.map((id) => renderPrevozRow(id, prevozyById.get(id) || {}))}
        </div>

        <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
          <b>Součet km:</b> {totalKm || 0}
        </div>
      </div>

      {/* RAKVE */}
      <div style={cardStyle}>
        <div style={titleStyle}>Rakev (ze skladu)</div>
        <div style={textStyle}>
          <div>
            <b>Vybraná rakev:</b> {pick(rakve?.typRakve, rakve?.nazev, "—")}
            {(() => {
              const rakveX = formData?.rakve || {};
              const skladX = formData?.skladRakvi || [];
              const cenyRakve = spoctiRakevCeny(
                {
                  id: rakveX?.id,
                  nazev: rakveX?.nazev || rakveX?.typRakve,
                  cenaBezDPH: rakveX?.cenaBezDPH,
                  sazbaDPH: rakveX?.sazbaDPH,
                },
                skladX
              );

              if (!cenyRakve || !(Number(cenyRakve.cenaBezDPH) > 0)) return null;

              return (
                <div style={{ marginTop: 6 }}>
                  <div>
                    <b>Rakev bez DPH:</b> {cenyRakve.cenaBezDPH} Kč
                  </div>
                  <div>
                    <b>DPH ({cenyRakve.sazbaDPH} %):</b> {cenyRakve.dph} Kč
                  </div>
                  <div>
                    <b>Rakev s DPH:</b> {cenyRakve.cenaSDPH} Kč
                  </div>
                </div>
              );
            })()}
          </div>
          <div>
            <b>Kód:</b> {pick(rakve?.kod, "—")}
          </div>
          <div>
            <b>Cena bez DPH:</b> {rakveBez ? `${rakveBez} Kč` : "—"}
          </div>
          <div>
            <b>DPH:</b> {rakveBez ? `${rakveDph} Kč (${rakveDphPct}%)` : "—"}
          </div>
          <div>
            <b>Cena s DPH:</b> {rakveBez ? `${rakveSDph} Kč` : "—"}
          </div>
        </div>
      </div>

      {/* CHLAZENÍ */}
      <div style={cardStyle}>
        <div style={titleStyle}>Chlazení (lednice / mrazák)</div>
        <div style={textStyle}>
          <div>
            <b>Úmrtí:</b> {datumUmrti || "—"}
            {casUmrti ? ` • ${casUmrti}` : ""}
          </div>

          <div>
            <b>Start (uložení do chladírny):</b> {pick(prevoz1?.datum, "—")}
            {prevoz1?.cas ? ` • ${prevoz1.cas}` : ""}
          </div>

          <div>
            <b>Konec (obřad / kremace):</b>{" "}
            {chlEnd
              ? `${datumEvent || "—"}${casEvent ? ` • ${casEvent}` : ""}`
              : "—"}
          </div>

          <div style={{ marginTop: 8 }}>
            <div>
              <b>Limit lednice (úmrtí + 7 dní):</b>{" "}
              {cooling.fridgeDeadlineDT ? fmtDT(cooling.fridgeDeadlineDT) : "—"}
            </div>
            <div>
              <b>Lednice do:</b> {cooling.fridgeEndDT ? fmtDT(cooling.fridgeEndDT) : "—"}
            </div>
            <div>
              <b>Mrazák od:</b>{" "}
              {cooling.freezerStartDT ? fmtDT(cooling.freezerStartDT) : "—"}
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            <div>
              <b>Dny lednice (započaté):</b> {daysLednice === null ? "—" : daysLednice}
            </div>
            <div>
              <b>Dny mrazák (započaté):</b> {daysMrazak === null ? "—" : daysMrazak}
            </div>
          </div>
        </div>
      </div>

      {/* VYBRANÉ SLUŽBY */}
      <div style={cardStyle}>
        <div style={titleStyle}>Vybrané služby</div>

        {(Array.isArray(sluzbyWithTotals) ? sluzbyWithTotals : []).length === 0 ? (
          <div style={{ opacity: 0.8 }}>Zatím nejsou propsané žádné služby.</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {sluzbyWithTotalsSafe.map((s) => (
              <div
                key={s.id}
                style={{
                  border: "1px solid #444",
                  borderRadius: 10,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontWeight: 800 }}>{s.nazev || s.id}</div>
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    <b>DPH:</b> {Number(s.dphPct) || 0}%
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    fontSize: 13,
                  }}
                >
                  <div>
                    <b>Množství:</b> {s.mnozstvi} {s.jednotka || ""}
                  </div>
                  <div>
                    <b>Cena/jedn bez DPH:</b> {s.cenaJednBezDph} Kč
                  </div>
                  <div>
                    <b>Bez DPH:</b> {s.bezDph} Kč
                  </div>
                  <div>
                    <b>DPH:</b> {s.dphKc} Kč
                  </div>
                  <div>
                    <b>Celkem:</b> {s.sDph} Kč
                  </div>
                </div>
              </div>
            ))}

            <hr style={{ margin: "10px 0" }} />
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div>
                <b>Součet bez DPH:</b> {sluzbyTotalBez} Kč
              </div>
              <div>
                <b>DPH:</b> {sluzbyTotalDph} Kč
              </div>
              <div>
                <b>Celkem s DPH:</b> {sluzbyTotalSDph} Kč
              </div>
            </div>
          </div>
        )}
      </div>

      {/* KVĚTINY */}
      <div style={cardStyle}>
        <div style={titleStyle}>Květiny</div>

        {kvetinyRows.length === 0 ? (
          <div style={{ opacity: 0.8 }}>Zatím nejsou zadané žádné květiny.</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {kvetinyRows.map((r, idx) => (
              <div
                key={r.id || String(idx)}
                style={{
                  border: "1px solid #444",
                  borderRadius: 10,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontWeight: 800 }}>{r.nazev || "Položka květin"}</div>
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    <b>DPH:</b> {r.dph || 0}%
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    fontSize: 13,
                  }}
                >
                  <div>
                    <b>Počet:</b> {r.pocet}
                  </div>
                  <div>
                    <b>Cena/ks bez DPH:</b> {r.cenaKs} Kč
                  </div>
                  <div>
                    <b>Cena/ks s DPH:</b> {r.cenaKsSDPH} Kč
                  </div>
                  <div>
                    <b>Bez DPH:</b> {r.bezDph} Kč
                  </div>
                  <div>
                    <b>DPH:</b> {r.dphKc} Kč
                  </div>
                  <div>
                    <b>Celkem:</b> {r.sDph} Kč
                  </div>
                </div>

                {r.poznamka ? (
                  <div style={{ marginTop: 6, fontSize: 13, opacity: 0.9 }}>
                    <b>Poznámka:</b> {r.poznamka}
                  </div>
                ) : null}
              </div>
            ))}

            <hr style={{ margin: "10px 0" }} />
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div>
                <b>Součet bez DPH:</b> {kvetinyTotalBez} Kč
              </div>
              <div>
                <b>DPH:</b> {kvetinyTotalDph} Kč
              </div>
              <div>
                <b>Celkem s DPH:</b> {kvetinyTotalSDph} Kč
              </div>
            </div>

            {String(kvetiny?.poznamka || "").trim() ? (
              <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                <b>Celková poznámka ke květinám:</b> {String(kvetiny.poznamka).trim()}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* REKAPITULACE */}
      <div style={{ border: "2px solid #444", borderRadius: 10, padding: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 6, fontSize: 16 }}>
          Rekapitulace
        </div>

        <div style={{ display: "grid", gap: 6, fontSize: 14 }}>
          <div>
            <b>Služby celkem s DPH:</b> {sluzbyTotalSDph} Kč
          </div>
          <div>
            <b>Květiny celkem s DPH:</b> {kvetinyTotalSDph} Kč
          </div>
          <div>
            <b>Rakev s DPH:</b> {rakveBez ? `${rakveSDph} Kč` : "—"}
          </div>
          <div style={{ fontSize: 16, marginTop: 4 }}>
            <b>CELKEM s DPH:</b> {grandSDph} Kč
          </div>
        </div>

        <div style={{ marginTop: 6, opacity: 0.85, fontSize: 13 }}>
          (Bez DPH: {grandBez} Kč • DPH: {grandDph} Kč)
        </div>
      </div>
    </div>
  );
}
